import { useState, useEffect, useCallback } from "react";
import { getSupabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export interface Notification {
  id: number;
  user_id: string;
  type: "project_approved" | "project_rejected" | "project_submitted" | "admin_new_project";
  title: string;
  message: string;
  related_project_id: number | null;
  is_read: boolean;
  created_at: string;
}

interface PendingProject {
  id: number;
  title: string;
  category: string;
  created_at: string;
}

interface UseNotificationsReturn {
  count: number;
  notifications: Notification[];
  pendingProjects: PendingProject[];
  loading: boolean;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useNotifications(): UseNotificationsReturn {
  const { user, profile } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [pendingProjects, setPendingProjects] = useState<PendingProject[]>([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = profile?.role === "admin";

  const fetchData = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setPendingProjects([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const supabase = getSupabase();

    try {
      if (isAdmin) {
        // Admin: fetch pending review projects
        const { data: pending } = await supabase
          .from("projects")
          .select("id, title, category, created_at")
          .eq("review_status", "pending")
          .order("created_at", { ascending: false })
          .limit(20);

        setPendingProjects(pending || []);

        // Also fetch recent notifications for admin (if any were inserted for them)
        const { data: notifs } = await supabase
          .from("notifications")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(20);

        setNotifications(notifs || []);
      } else {
        // Regular user: fetch their notifications
        const { data: notifs } = await supabase
          .from("notifications")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(20);

        setNotifications(notifs || []);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    fetchData();

    // Poll every 30 seconds for new notifications / pending count
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const markAsRead = useCallback(async (id: number) => {
    const supabase = getSupabase();
    try {
      await supabase.from("notifications").update({ is_read: true }).eq("id", id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    } catch {
      // silent
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    const supabase = getSupabase();
    try {
      await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("user_id", user.id)
        .eq("is_read", false);

      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch {
      // silent
    }
  }, [user]);

  // Count: for admins = pending projects count, for users = unread notifications count
  const count = isAdmin
    ? pendingProjects.length
    : notifications.filter((n) => !n.is_read).length;

  return {
    count,
    notifications,
    pendingProjects,
    loading,
    markAsRead,
    markAllAsRead,
    refresh: fetchData,
  };
}